import { APP_BASE_HREF, CurrencyPipe, DatePipe, PlatformLocation, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import { DEFAULT_CURRENCY_CODE, LOCALE_ID, NgModule } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCardModule as MatCardModule } from '@angular/material/legacy-card';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyListModule as MatListModule } from '@angular/material/legacy-list';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyPaginatorIntl as MatPaginatorIntl } from '@angular/material/legacy-paginator';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatLegacyTableModule as MatTableModule } from '@angular/material/legacy-table';
import { MatLegacyTabsModule as MatTabsModule } from '@angular/material/legacy-tabs';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MAT_DRAWER_DEFAULT_AUTOSIZE, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { ApiService, OpenModelServletService, QUERY_INVOKER, SFX_SERVICE_INVOKER } from '@ebootis/common';
import { ChangePasswordIntl, LoginIntl, LoginLibModule, RESET_PASSWORD_ROUTE, REVISION, ResetPasswordDialogIntl, ResetPasswordIntl, UserDevicesIntl } from '@ebootis/login';
import { MessageHandlerIntl, NotificationHandlerIntl, STORAGE_CONFIG, UtilitiesModule } from "@ebootis/utilities";
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { revision } from '../assets/revision';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './components/app/app.component';
import { ConfigurationSelectComponent } from './components/configuration-select/configuration-select.component';
import { ConfigurationService } from './components/configuration-select/configuration.service';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { Error504Component } from './components/error504/error504.component';
import { ImprintComponent } from './components/imprint/imprint.component';
import { InfoDialogComponent } from './components/info-dialog/info-dialog.component';
import { LoginComponent } from './components/login/login.component';
import { LogoutService } from './components/login/logout.service';
import { ResetPasswordComponent } from './components/reset-password/reset-password.component';
import { ScrollTopButtonComponent } from './components/scroll-top-button/scroll-top-button.component';
import { SettingsComponent } from './components/settings/settings.component';
import { UserInfoComponent } from './components/user-info/user-info.component';
import { CustomDateAdapter } from './custom-date-adapter';
import { MessageHandlerI18n } from './i18n/MessageHandlerI18n';
import { NotificationHandlerI18n } from './i18n/NotificationHandlerI18n';
import { PaginatorI18n } from './i18n/PaginatorI18n';
import { ChangePasswordI18n } from './i18n/login/ChangePasswordI18n';
import { LoginI18n } from './i18n/login/LoginI18n';
import { LoginRpwI18n } from './i18n/login/LoginRpwI18n';
import { ResetPwDialogI18n } from './i18n/login/ResetPwDialogI18n';
import { UserDevicesI18n } from './i18n/login/UserDevicesI18n';
import { OrderModule } from './modules/order/order.module';
import { LogisticAreaService } from './modules/shared/logistic-area/logistic-area.service';
import { SharedModule } from './modules/shared/shared.module';
import { PrintLayoutComponent } from './print-layout/print-layout.component';
import { CustomMultiTranslateHttpLoader } from './services/custom-multi-translate-http-loader';
import { HttpAuthInterceptor } from './services/http.auth.interceptor';
import { StorageConfigService } from './services/storage-config.service';

export function getRevision(): string {
	console.log('revision:' + revision.revision);
	return revision.revision;
}
export function createTranslateLoader(http: HttpClient) {
	return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function HttpLoaderFactory(httpClient: HttpClient, configService: ConfigurationService) {
	let ressources = [
		{ prefix: './assets/i18n/', suffix: '.json' },
		{ prefix: './assets/i18n/specific_', suffix: '.json' },
	];
	return new CustomMultiTranslateHttpLoader(httpClient, ressources, configService);
}

export function getBaseHref(platformLocation: PlatformLocation): string {
	return platformLocation.getBaseHrefFromDOM();
}
export function getResetPasswordRoute(platformLocation: PlatformLocation): string {
	return getBaseHref(platformLocation) + 'zuruecksetzen';
}

export function getDBName(plattformLocation: PlatformLocation) {
	let baseHref = getBaseHref(plattformLocation);
	baseHref = baseHref.slice(1, baseHref.length - 1);
	return baseHref.replace(/\//g, '_');
}

registerLocaleData(localeDe, 'de');

@NgModule({
	declarations: [
		AppComponent,
		LoginComponent,
		ConfigurationSelectComponent,
		SettingsComponent,
		DashboardComponent,
		ImprintComponent,
		Error504Component,
		UserInfoComponent,
		ResetPasswordComponent,
		PrintLayoutComponent, ScrollTopButtonComponent, InfoDialogComponent
	],
	imports: [
		BrowserAnimationsModule,
		HttpClientModule,
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: HttpLoaderFactory,
				deps: [HttpClient, ConfigurationService]
			}
		}),
		AppRoutingModule,
		ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
		LoginLibModule.forRoot(),
		MatSidenavModule,
		MatIconModule,
		MatListModule,
		MatToolbarModule,
		MatCardModule,
		MatButtonModule,
		MatCheckboxModule,
		MatMenuModule,
		SharedModule,
		MatFormFieldModule,
		MatInputModule,
		MatTableModule,
		MatTooltipModule,
		UtilitiesModule,
		MatTabsModule,
		MatDialogModule,
		MatBadgeModule,
		OrderModule,
	],
	providers: [
		ConfigurationService,
		LogoutService,
		CurrencyPipe,
		LogisticAreaService,
		TranslateService,
		DatePipe,
		{
			provide: MAT_DRAWER_DEFAULT_AUTOSIZE,
			useValue: true,
		},
		{
			provide: REVISION,
			useFactory: getRevision
		},
		{
			provide: APP_BASE_HREF,
			useFactory: getBaseHref,
			deps: [PlatformLocation],
		},
		{
			provide: QUERY_INVOKER,
			useClass: OpenModelServletService,
			deps: [ApiService],
		},
		{
			provide: SFX_SERVICE_INVOKER,
			useClass: OpenModelServletService,
			deps: [ApiService],
		},
		{
			provide: MatPaginatorIntl,
			useClass: PaginatorI18n,
		},
		{
			provide: MessageHandlerIntl,
			useClass: MessageHandlerI18n,
		},
		{
			provide: NotificationHandlerIntl,
			useClass: NotificationHandlerI18n,
		},
		{
			provide: LoginIntl,
			useClass: LoginI18n,
		},
		{
			provide: ResetPasswordIntl,
			useClass: LoginRpwI18n,
		},
		{
			provide: ResetPasswordDialogIntl,
			useClass: ResetPwDialogI18n,
		},
		{
			provide: ChangePasswordIntl,
			useClass: ChangePasswordI18n,
		},
		{
			provide: UserDevicesIntl,
			useClass: UserDevicesI18n,
		},
		// provide translation for Material Datepicker
		{
			provide: MAT_DATE_LOCALE,
			useValue: 'de',
		},
		// provide translation for Currency
		{
			provide: LOCALE_ID,
			useValue: 'de-De',
		},
		{ provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
		{ provide: DateAdapter, useClass: CustomDateAdapter },
		{
			provide: HTTP_INTERCEPTORS,
			useClass: HttpAuthInterceptor,
			deps: [Router, LogoutService, MatSnackBar],
			multi: true,
		},
		{
			provide: RESET_PASSWORD_ROUTE,
			useFactory: getResetPasswordRoute,
			deps: [PlatformLocation],
		},
		{
			provide: STORAGE_CONFIG,
			useClass: StorageConfigService
		}
	],
	bootstrap: [AppComponent],
})
export class AppModule { }
